import './App.css';
import {useEffect, useRef, useState} from "react";

import {FiMic, FiPause} from "react-icons/fi";

import {motion} from "framer-motion";

import {jpnVisemeToImageClipSequence} from "./VisemeUtils";
import {extractFramesFromH264} from "./VideoUtils";

import { MicVAD } from "@ricky0123/vad-web"
import {BiMicrophone, BiPause, BiX} from "react-icons/bi";
import {pcmHexToWavBlob} from "./AudioUtils";
import {audio} from "framer-motion/m";

// 전역 AudioContext (Web Audio로 재생할 때 필수)
export const ctx = new (window.AudioContext || window.webkitAudioContext)();

// 한 번만 호출되는 해제 함수
function unlockAudio() {
    if (ctx.state === 'suspended') ctx.resume();      // Web Audio용
    // HTMLMediaElement도 미리 살짝 재생 시도해 두면 더 안전
    const probe = new Audio();
    probe.muted = true;
    probe.play().catch(() => {/* 무시 */});

    window.removeEventListener('pointerup', unlockAudio);
}

// 페이지 어디든 첫 pointerup(클릭·터치)에서 잠금 해제
window.addEventListener('pointerup', unlockAudio, { once: true });

export async function speak(blob) {
    // 1) unlockAudio() 가 이미 실행돼 ctx.state === 'running'
    const buf = await blob.arrayBuffer();
    const audioBuf = await ctx.decodeAudioData(buf);

    const src = ctx.createBufferSource();
    src.buffer = audioBuf;
    src.connect(ctx.destination);
    src.start();
}

let gl = null;

const programCache = {};
const textureCache = {};
const bufferCache = {};

let configData = null;

const eyeQueue = [];
const lipQueue = [];

const previewZip = {};

let previewLoaded = false;

let lipIdleFrame = 0;
let eyeIdleFrame = 0;

const lipIdleAnim = [];
const lipAnimation = [];

let lipAnimationEnabled = false;

const eyeIdleAnim = [];
const eyeAnimation = [];

const SPEECH_REGION = "koreacentral";
const SPEECH_LANGUAGE = "ja-JP";
const SPEECH_KEY = process.env.REACT_APP_SPEECH_KEY;

let pcmTimeout = null;

let myvad = null;

/**
 * float32ArrayToWavBlob
 * ---------------------
 * @param samples     Float32Array  [-1.0, 1.0]
 * @param sampleRate  number        e.g. 48000
 * @returns           Blob          audio/wav
 */
function float32ArrayToWavBlob(samples, sampleRate) {
    // 16-bit PCM으로 양자화
    const pcm16 = new Int16Array(samples.length);
    for (let i = 0; i < samples.length; i++) {
        const s = Math.max(-1, Math.min(1, samples[i]));   // clip
        pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    const byteRate   = sampleRate * 2;          // mono, 16-bit
    const blockAlign = 2;

    // --- WAV RIFF 헤더(44바이트) 작성 ---
    const buffer = new ArrayBuffer(44 + pcm16.byteLength);
    const view   = new DataView(buffer);

    let offset = 0;
    const writeStr = (str) => {
        for (let i = 0; i < str.length; i++) view.setUint8(offset++, str.charCodeAt(i));
    };
    const write32 = (val) => (view.setUint32(offset, val, true), offset += 4);
    const write16 = (val) => (view.setUint16(offset, val, true), offset += 2);

    writeStr('RIFF');                               // ChunkID
    write32(36 + pcm16.byteLength);                 // ChunkSize
    writeStr('WAVE');                               // Format
    writeStr('fmt '); write32(16);                  // Subchunk1ID & Size
    write16(1);                                     // AudioFormat (1=PCM)
    write16(1);                                     // NumChannels (mono)
    write32(sampleRate);                            // SampleRate
    write32(byteRate);                              // ByteRate
    write16(blockAlign);                            // BlockAlign
    write16(16);                                    // BitsPerSample
    writeStr('data'); write32(pcm16.byteLength);    // Subchunk2ID & Size

    // PCM 데이터 복사
    new Uint8Array(buffer, 44).set(new Uint8Array(pcm16.buffer));

    return new Blob([buffer], { type: 'audio/wav' });
}

async function speechToText(buffer) {
    const endpoint =
        `https://${SPEECH_REGION}.stt.speech.microsoft.com/` +
        `speech/recognition/conversation/cognitiveservices/v1?language=${SPEECH_LANGUAGE}&format=detailed`;

    const r = await fetch(endpoint, {
        method: "POST",
        headers: {
            "Ocp-Apim-Subscription-Key": SPEECH_KEY,
            "Content-Type": "audio/wav",
        },
        body: buffer
    });

    if (!r.ok) {
        console.error("Error:", await r.text());
        throw new Error(r.statusText);
    }

    const data = await r.json();

    console.log("Received response", data);

    const { Display } = data.NBest?.[0] ?? {};
    return Display ?? "";
}

// initialize canvas for webgl
function initCanvas() {
    const canvas = document.getElementById("canvas");
    if (!canvas) return;

    gl = canvas.getContext("webgl");
    if (!gl) {
        console.error("WebGL not supported, falling back to experimental-webgl");
        return;
    }

    canvas.width = 1080;
    canvas.height = 1920;

    // Set the viewport to match the canvas size
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0.0, 0.0, 0.0, 0.0); // Set clear color to black
    gl.clear(gl.COLOR_BUFFER_BIT); // Clear the color buffer
}

function loadShader(type, source) {
    console.log("Loading shader of type:", type);

    const shader = gl.createShader(type);
    if (!shader) {
        console.error("Unable to create shader");
        return null;
    }
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error("An error occurred compiling the shaders: " + gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
    }

    console.log("Shader compiled successfully");
    return shader;
}

function createProgram(vertexShaderSource, fragmentShaderSource) {
    const vertexShader = loadShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = loadShader(gl.FRAGMENT_SHADER, fragmentShaderSource);

    if (!vertexShader || !fragmentShader) {
        console.error("Failed to create shaders");
        return null;
    }

    const program = gl.createProgram();
    if (!program) {
        console.error("Unable to create program");
        return null;
    }

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error("Unable to initialize the shader program: " + gl.getProgramInfoLog(program));
        gl.deleteProgram(program);
        return null;
    }

    return program;
}

// load image as texture
async function loadTexture(url) {
    const image = new Image();

    if (previewZip[url]) {
        console.log("Using cached preview image for:", url);
        image.src = previewZip[url];
    } else {
        image.src = url;
    }

    return new Promise((resolve, reject) => {
        image.onload = () => {
            const texture = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

            console.log("Texture loaded successfully:", url);

            resolve(texture);
        };
        image.onerror = (err) => {
            console.error("Failed to load texture:", err);
            reject(err);
        };
    });
}

async function loadImageWithPreview(key, path) {
    const texture = await loadTexture(path + ".png");

    if (!texture) {
        console.error("Failed to load preview texture for:", key);
        throw new Error("Failed to load preview texture");
    }

    textureCache[key] = texture;
    //
    // console.log("Preview texture loaded for:", key);
    //
    // (async () => {
    //     // wait for previewLoaded to be true
    //     while (!previewLoaded) {
    //         await new Promise(resolve => setTimeout(resolve, 1000));
    //     }
    //
    //     // Load the main texture
    //     const mainTexture = await loadTexture(path + ".png");
    //     if (!mainTexture) {
    //         console.error("Failed to load main texture for:", key);
    //         throw new Error("Failed to load main texture");
    //     }
    //
    //     textureCache[key] = mainTexture;
    //     console.log("Main texture loaded for:", key);
    // })().catch(err => {
    //     console.error("Error loading main texture for:", key, err);
    // });
}

async function loadVideoTexture(keys, path, cropWidth = 0, cropHeight = 0) {
    const frames = await extractFramesFromH264(gl, path, cropWidth, cropHeight);

    console.log(`NUM FRAMES: ${frames.length}, NUM KEYS: ${keys.length}`);

    if (!frames || frames.length === 0) {
        console.error("Failed to extract frames from video:", path);
        throw new Error("Failed to extract frames from video");
    }

    if (frames.length !== keys.length) {
        console.warn(`Number of frames (${frames.length}) does not match number of keys (${keys.length})`);
    }

    for (let i = 0; i < frames.length; i++) {
        const key = keys[i];
        const frame = frames[i];

        if (!frame) {
            console.error(`Frame ${i} is invalid for key: ${key}`);
            continue;
        }

        textureCache[key] = frame;
        console.log(`Video texture loaded for key: ${key}`);
    }
}

function ThinkingIndicator() {
    return (
        <motion.span
            className="h-3 w-3 rounded-full thinking-indicator"
            animate={{
                scale: [1, 1.4, 1],      // 살짝 커졌다 돌아오고
                opacity: [1, 0.4, 1],    // 동시에 투명도 변화
            }}
            transition={{
                duration: 1.2,           // 한 사이클 1.2초
                repeat: Infinity,        // 무한 반복
                ease: "easeInOut",
            }}
        />
    );
}

async function fetchMessage(message, token) {
    const response = await fetch("/api/chat", {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({message, token})
    });

    if (!response.ok) {
        throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data;
}

let prevDt = 0;
let eyeFrameDt = 0;
let lipFrameDt = 0;

function render(dt) {
    const deltaTime = dt - prevDt;
    prevDt = dt;

    if (deltaTime === 0) {
        return;
    }

    // console.log("Rendering frame...");
    // clear the canvas
    const canvas = document.getElementById("canvas");
    if (!canvas) return;

    gl = canvas.getContext("webgl");
    if (!gl) {
        console.error("WebGL not supported, falling back to experimental-webgl");
        return;
    }

    let eyeTexture = textureCache["eye_idle"];
    let lipTexture = textureCache["lip_idle"];

    const eyeFrameTime = dt - eyeFrameDt;

    if (eyeAnimation.length > 0) {
        eyeIdleFrame = 0; // Reset idle frame when an animation is playing

        const eyeAnim = eyeAnimation[0];
        eyeTexture = textureCache[eyeAnim[1]];

        if (eyeFrameTime > eyeAnim[0]) {
            eyeFrameDt += eyeAnim[0]; // Update the frame time
            eyeAnimation.shift(); // Remove the first animation frame
        }
    } else {
        const eyeAnim = eyeIdleAnim[eyeIdleFrame % eyeIdleAnim.length];
        eyeTexture = textureCache[eyeAnim[1]];

        if (eyeFrameTime > eyeAnim[0]) {
            eyeIdleFrame++;
            eyeFrameDt = dt;
        }
    }

    const lipFrameTime = dt - lipFrameDt;

    if (lipAnimationEnabled && lipAnimation.length > 0) {
        lipIdleFrame = 0; // Reset idle frame when an animation is playing

        const lipAnim = lipAnimation[0];
        lipTexture = textureCache[lipAnim[1]];

        // console.log("Current lip animation frame:", lipAnim[1], "Duration:", lipAnim[0], "Frame time:", lipFrameTime);

        if (lipFrameTime > lipAnim[0]) {
            lipFrameDt += lipAnim[0]; // Update the frame time
            lipAnimation.shift(); // Remove the first animation frame
        }
    } else {
        const lipAnim = lipIdleAnim[lipIdleFrame % lipIdleAnim.length];
        lipTexture = textureCache[lipAnim[1]];
        if (lipFrameTime > lipAnim[0]) {
            lipIdleFrame++;
            lipFrameDt = dt;
        }
    }

    // Set the viewport to match the canvas size
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0.0, 0.0, 0.0, 0.0); // Set clear color to black
    gl.clear(gl.COLOR_BUFFER_BIT); // Clear the color buffer

    // // 1) 블렌딩 기능 켜기
    // gl.enable(gl.BLEND);
    //
    // // 2) 소스‑알파 vs 대상‑알파 가중치 설정
    // //    가장 흔한 ‘표준’ 알파 블렌드
    // gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    // Use the default program
    const program = programCache["default"];
    if (!program) {
        console.error("No default program found");
        return;
    }

    gl.useProgram(program);

    gl.bindBuffer(gl.ARRAY_BUFFER, bufferCache["default"]);

    const positionLocation = gl.getAttribLocation(program, "aVertexPosition");
    if (positionLocation < 0) {
        console.error("Failed to get attribute location for aVertexPosition");
        return;
    }
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    gl.uniform2f(gl.getUniformLocation(program, "uScreenSize"), canvas.width, canvas.height);

    // bind mask texture if available
    if (textureCache["mask"]) {
        const maskTexture = textureCache["mask"];
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, maskTexture);
        const uMaskSampler = gl.getUniformLocation(program, "uMaskSampler");
        if (uMaskSampler) {
            gl.uniform1i(uMaskSampler, 1); // Texture unit 1 for mask
        } else {
            console.error("Failed to get uniform location for uMaskSampler");
        }
        gl.activeTexture(gl.TEXTURE0); // Switch back to texture unit 0 for the main texture
    }

    // set mask enabled to 0
    const uMaskEnabled = gl.getUniformLocation(program, "uMaskEnabled");
    if (uMaskEnabled) {
        gl.uniform1i(uMaskEnabled, 0);
    } else {
        console.error("Failed to get uniform location for uMaskEnabled");
    }

    // Bind the texture if available
    {
        const texture = textureCache["body"];
        if (texture) {
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), 0, 0, canvas.width, canvas.height);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    // enable mask
    gl.uniform1i(uMaskEnabled, 1);

    {
        const texture = textureCache["background_image"];
        if (texture) {
            gl.bindTexture(gl.TEXTURE_2D, texture);
            const right = 1030;
            const bottom = 1050;
            const left = right - 1000;
            const top = bottom - 1000;
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), left, top, right - left, bottom - top);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    {
        if (lipTexture) {
            gl.bindTexture(gl.TEXTURE_2D, lipTexture);
            const left = 396;
            const top = 539;
            const width = configData.coordinates.lower_face.lower_right[0] - configData.coordinates.lower_face.upper_left[0];
            const height = configData.coordinates.lower_face.lower_right[1] - configData.coordinates.lower_face.upper_left[1];
            // console.log("Lip idle texture coordinates:", left, top, width, height);
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), left, top, width, height);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    {
        if (eyeTexture) {
            gl.bindTexture(gl.TEXTURE_2D, eyeTexture);
            const left = 109;
            const top = 51;
            const width = configData.coordinates.upper_face.lower_right[0] - configData.coordinates.upper_face.upper_left[0];
            const height = configData.coordinates.upper_face.lower_right[1] - configData.coordinates.upper_face.upper_left[1];
            // console.log("Eye idle texture coordinates:", left, top, width, height);
            gl.uniform4f(gl.getUniformLocation(program, "uImageCoord"), left, top, width, height);
            const uSampler = gl.getUniformLocation(program, "uSampler");
            if (uSampler) {
                gl.uniform1i(uSampler, 0); // Texture unit 0
            } else {
                console.error("Failed to get uniform location for uSampler");
            }
            // Draw the rectangle
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
    }

    // Unbind the buffer
    // gl.bindBuffer(gl.ARRAY_BUFFER, null);
    // gl.bindTexture(gl.TEXTURE_2D, null);

    // Cleanup
    // gl.useProgram(null);
}

function ChatBotLog({log, onEnded, flush}) {
    const [currentLog, setCurrentLog] = useState("");

    let index = useRef(0);

    // Effect to handle typing effect
    useEffect(() => {
        const typingInterval = setInterval(() => {
            if (flush) {
                clearInterval(typingInterval);
                setCurrentLog(log);
                onEnded();
                return;
            }

            if (index.current < log.length) {
                const c = log[index.current];

                if (c) {
                    setCurrentLog((prev) => prev + c);
                }

                onEnded();
                index.current += 1;
            } else {
                onEnded();
                clearInterval(typingInterval);
            }
        }, 50); // Adjust typing speed here

        return () => clearInterval(typingInterval);
    }, [log, onEnded, flush]);

    return (
        <div className={"msg log-bot"}>
            {currentLog}
        </div>
    )
}

function App() {
    const [isLoading, setIsLoading] = useState(true);
    const [logs, setLogs] = useState([]);
    const [isActive, setIsActive] = useState(false);
    const [inputText, setInputText] = useState("");
    const [userInfo, setUserInfo] = useState(null);
    const [audioData, setAudioData] = useState(null);
    const [animQueue, setAnimQueue] = useState([]);
    const [playAnimation, setPlayAnimation] = useState(false);

    const [mode, setmode] = useState("chat");

    const [roudness, setRoudness] = useState(0);

    const personName = "澁谷 毅";

    useEffect(() => {
        (async () => {
            let savedUserId = localStorage.getItem("userId");

            if (!savedUserId) {
                // Generate a new user ID if not found
                savedUserId = crypto.randomUUID().toString();
                localStorage.setItem("userId", savedUserId);
            }

            let loginFetch = fetch("/api/login", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({userId: savedUserId})
            });

            console.log("Initializing WebGL...");
            // Initialize the canvas for WebGL
            initCanvas();

            if (!gl) {
                console.error("Failed to initialize WebGL context");
                return;
            }

            // load a texture
            await Promise.all([
                loadImageWithPreview("body", "/data/body_image"),
                loadImageWithPreview("background_image", "/data/background_image"),
                loadImageWithPreview("mask", "/data/mask"),
                loadImageWithPreview("eye_idle", "/data/eye_idle"),
                loadImageWithPreview("lip_idle", "/data/lip_idle"),
                (async () => {
                    const configJson = await fetch("/data/config.json");

                    if (!configJson.ok) {
                        console.error("Failed to fetch config.json:", configJson.statusText);
                        return;
                    }

                    configData = await configJson.json();

                    console.log("Config data loaded:", configData);
                })(),
            ]);

            // Load shaders and create program
            const vertexShaderSource = `
            precision mediump float;
            
            attribute vec4 aVertexPosition;
            
            varying vec2 vTextureCoord;
            varying vec2 vMaskCoord;
            
            uniform vec2 uScreenSize; // screen size
            uniform vec4 uImageCoord; // xywh coordinates for the image
            
            void main(void) {            
                // Set the position of the vertex
                gl_Position = vec4(aVertexPosition.xy, 0.0, 1.0);
                gl_Position.xy = gl_Position.xy * (uImageCoord.zw / uScreenSize);
                gl_Position.xy = gl_Position.xy + vec2(-1.0, 1.0) + vec2(1.0, -1.0) * uImageCoord.zw / uScreenSize.xy;
                gl_Position.xy = gl_Position.xy + vec2(1.0, -1.0) * uImageCoord.xy / uScreenSize.xy * 2.0;
                
                vMaskCoord = (gl_Position.xy * vec2(1, -1) + vec2(1.0, 1.0)) / 2.0; // Normalize to [0, 1]
                
                vTextureCoord = (aVertexPosition.xy * vec2(1, -1) + vec2(1.0, 1.0)) / 2.0; // Normalize to [0, 1]
            }`;
            const fragmentShaderSource = `
            precision mediump float;
            
            varying vec2 vTextureCoord;
            varying vec2 vMaskCoord;
            
            uniform sampler2D uSampler;
            uniform sampler2D uMaskSampler; // Mask texture
            
            uniform int uMaskEnabled; // 0 or 1
            
            void main(void) {
                if (uMaskEnabled == 1) {
                    vec4 maskColor = texture2D(uMaskSampler, vMaskCoord);
                    if (maskColor.r < 0.1) {
                        discard; // Discard pixels based on mask
                    }
                }
                
                // gl_FragColor = vec4(vTextureCoord.xy, 0.0, 1.0); // Simple color based on texture coordinates
                gl_FragColor = texture2D(uSampler, vTextureCoord);
                if (gl_FragColor.a < 0.1) {
                    discard; // Discard transparent pixels
                }
            }`;

            const program = createProgram(vertexShaderSource, fragmentShaderSource);

            if (!program) {
                console.error("Failed to create shader program");
                return;
            }

            programCache["default"] = program;

            // Set up the vertex positions
            const vertices = new Float32Array([
                -1.0, -1.0,
                1.0, -1.0,
                -1.0, 1.0,
                1.0, 1.0,
            ]);
            const vertexBuffer = gl.createBuffer();
            if (!vertexBuffer) {
                console.error("Failed to create vertex buffer");
                return;
            }

            gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

            bufferCache["default"] = vertexBuffer;

            lipIdleAnim.push([
                10,
                "/data/lip_idle.png" // Default idle texture
            ]);

            eyeIdleAnim.push([
                5000, // 5 second for idle
                "/data/eye_idle.png" // Default idle texture
            ])

            // load keysJson
            const [lipKeysJson, eyeKeysJson] = await Promise.all([
                fetch("/data/lip.json"),
                fetch("/data/eye.json"),
            ]);

            // load lip textures
            if (!lipKeysJson.ok) {
                console.error("Failed to fetch lip.json:", lipKeysJson.statusText);
                return;
            }

            // load eye textures
            if (!eyeKeysJson.ok) {
                console.error("Failed to fetch eye.json:", eyeKeysJson.statusText);
                return;
            }
            const [lipKeys, eyeKeys] = await Promise.all([
                lipKeysJson.json(),
                eyeKeysJson.json(),
            ]);

            const eyeWidth = configData.coordinates.upper_face.lower_right[0] - configData.coordinates.upper_face.upper_left[0];
            const eyeHeight = configData.coordinates.upper_face.lower_right[1] - configData.coordinates.upper_face.upper_left[1];
            const lipWidth = configData.coordinates.lower_face.lower_right[0] - configData.coordinates.lower_face.upper_left[0];
            const lipHeight = configData.coordinates.lower_face.lower_right[1] - configData.coordinates.lower_face.upper_left[1];

            // Load video textures for lip and eye animations
            await Promise.all([
                loadVideoTexture(lipKeys, "/data/lip.h264", lipWidth, lipHeight),
                loadVideoTexture(eyeKeys, "/data/eye.h264", eyeWidth, eyeHeight),
            ]);

            console.log("cache textures:", Object.keys(textureCache));

            const animationData = configData.eye_resource["blink"];
            if (animationData && animationData.files) {
                console.log("Loading eye idle animation frames:", animationData.files);
                for (let i = 0; i < animationData.files.length; i++) {
                    const file = animationData.files[i];
                    const key = `${animationData.path}/${file}`;
                    eyeIdleAnim.push([1000 / 30, key]); // 30 FPS
                }
                console.log(`Eye idle animation frames loaded: ${eyeIdleAnim.length}`,);
            } else {
                console.error("No eye idle animation data found in config");
            }

            previewLoaded = true;

            const loginResponse = await loginFetch;

            if (!loginResponse.ok) {
                console.error("Failed to fetch user info:", loginResponse.statusText);
                return;
            }

            const data = await loginResponse.json();
            setUserInfo(data);
            console.log("User info fetched:", data);

            setIsActive(true);
            setIsLoading(false);

            // Start the render loop
            const renderLoop = (dt) => {
                render(dt);
                requestAnimationFrame(renderLoop);
            };
            requestAnimationFrame(renderLoop);

            // Cleanup function to stop the render loop
            return () => {
                cancelAnimationFrame(renderLoop);
            };
        })();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [logs]);

    async function sendRecording(blob) {
        if (!blob) {
            console.warn("No recording blob to send.");
            return;
        }

        console.log("Sending recording blob:", blob);

        try {
            const buffer = await blob.arrayBuffer();
            const data = await speechToText(buffer);
            console.log("Speech-to-text result:", data);

            if (data.trim() === "") {
                console.warn("Empty speech-to-text result, not submitting message.");
                setmode("recording");
                return;
            }

            await submitMessageInternal(data);
        } catch (error) {
            console.error("Error processing recording:", error);
        }
    }

    async function setAnimData({text, ttsData, visemes}) {
        const vd = jpnVisemeToImageClipSequence(visemes.map(v => {
            return {
                "id": v.id,
                "offset": v.ms,
            }
        }));

        console.log("Setting animation data:", vd, ttsData);
        lipAnimation.length = 0; // Clear previous lip animations

        const visemeLength = vd[vd.length - 1][1];
        const audioLength = ttsData.extra_info.audio_length;

        console.log("Viseme length:", visemeLength, "Audio length:", audioLength);

        const syncRatio = audioLength / visemeLength;
        console.log("Sync ratio calculated:", syncRatio);

        let lastEndTime = 0;
        let lastFile = "/data/lip_idle.png"; // Default idle texture

        for (let [startTime, endTime, clip, startFrame, endFrame, romaji] of vd) {
            const len = endTime - startTime;
            const clipData = configData.lip_resource[clip];

            if (lastEndTime < startTime) {
                // If there is a gap, add the idle frame
                if (lastFile) {
                    lipAnimation.push([
                        (startTime - lastEndTime) * syncRatio, // Duration for the idle frame
                        lastFile // Use the last file as the idle texture
                    ]);
                }
            }

            lastEndTime = endTime;

            if (endFrame === 0) {
                endFrame = clipData.files.length - 1; // Use the last frame if endFrame is 0
            }

            const files = clipData.files.slice(startFrame, endFrame + 1);

            const timePerFrame = len / files.length;

            for (let i = 0; i < files.length; i++) {
                lipAnimation.push([
                    timePerFrame * syncRatio, // Duration for each frame
                    `${clipData.path}/${files[i]}` // Full path to the texture
                ]);
                lastFile = `${clipData.path}/${files[i]}`; // Update last file
            }
        }

        // console.log("lip Animation: ", JSON.stringify(lipAnimation, null, 2));

        const timeSum = lipAnimation.reduce((sum, anim) => sum + anim[0], 0);
        console.log("Total lip animation time:", timeSum, "ms");

        setAudioData(ttsData.data.audio);
    }

    async function submitMessageInternal(inputText) {
        scrollToBottom();
        setIsActive(false);
        setLogs((prevLogs) => [...prevLogs, inputText]);

        try {
            const response = await fetchMessage(inputText, userInfo?.access);

            if (!response.animData) {
                response.animData = response.splitted.map((item, index) => {
                    return new Promise(async (resolve, reject) => {
                        try {
                            await new Promise(resolve => setTimeout(resolve, index * 200)); // Delay for each item

                            const r = await fetch("/api/tts", {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json"
                                },
                                body: JSON.stringify({
                                    text: item,
                                }),
                            });

                            if (!r.ok) {
                                throw new Error(`Failed to fetch TTS data: ${r.statusText}`);
                            }

                            resolve(await r.json());
                        } catch (error) {
                            console.error("Error processing item:", item, error);
                            reject(error);
                        }
                    });
                });
            }

            console.log("Response received:", response);

            const queue = response.animData;

            if (window.parentCallback) {
                console.log("Calling parent callback with response message:", response.message);
                window.parentCallback({
                    "type": "aime-chat-response",
                    "message": response.message,
                });
            }

            setAnimQueue(queue);

            (async () => {
                await queue[0];
                setLogs((prevLogs) => [...prevLogs, response.message]);
                setPlayAnimation(true);
                setIsActive(true);
            })();
        } catch (error) {
            console.error("Error fetching message:", error);
            setLogs((prevLogs) => [...prevLogs, `Error fetching response: ${error.message}`]);
            setIsActive(true);
        }
    }

    function scrollToBottom() {
        // scroll to the bottom of the chat logs whenever logs change
        const logsContainer = document.querySelector(".logs");
        if (logsContainer) {
            logsContainer.scrollTop = logsContainer.scrollHeight;
        }
    }

    async function submitMessage() {
        if (inputText.trim() === "") return;
        if (!isActive) return;

        setInputText("");

        await submitMessageInternal(inputText);
    }

    function onInputButton() {
        unlockAudio();
        switch (mode) {
            case "recording":
                setmode("pause");
                break;
            case "chat":
                setmode("recording");
                break;
            case "pause":
                setmode("chat");
                break;
        }
    }

    if (window.childCallbacks) {
        window.childCallbacks.chat = submitMessageInternal;
    }

    useEffect(() => {
        if (mode === "recording") {
            console.log("Starting voice recording...");
            (async () => {
                try {
                    myvad = await MicVAD.new({
                        onFrameProcessed: (probabilities, frame) => {
                            // console.log(probabilities, frame);
                            // compute roudness from frame data
                            const roudness = frame.reduce((sum, value) => sum + Math.abs(value), 0) / frame.length;
                            setRoudness(roudness);
                            // console.log("Roudness:", roudness);
                        },
                        onSpeechEnd: async (audio) => {
                            // do something with `audio` (Float32Array of audio samples at sample rate 16000)...
                            console.log("User stopped speaking, audio data:", audio);

                            const blob = float32ArrayToWavBlob(audio, 16000);
                            console.log("Converted audio to WAV blob:", blob);

                            myvad.pause();
                            myvad.destroy();
                            myvad = null;

                            setmode("pause");
                            await sendRecording(blob);
                        },
                        preSpeechPadFrames: 10,
                    })
                    myvad.start()
                } catch (error) {
                    console.error("Error starting voice recording:", error);
                    setmode("chat");
                }
            })();
        }
    }, [mode]);

    useEffect(() => {
        if (playAnimation) {
            setPlayAnimation(false);
        } else {
            return; // Do nothing if not playing animation
        }

        if (animQueue.length === 0) return;

        (async () => {
            const animData = await animQueue[0];

            setAnimData(animData);
            setAnimQueue(animQueue.slice(1));
        })();
    }, [animQueue, playAnimation]);

    useEffect(() => {
        if (!audioData) return;

        const blob = pcmHexToWavBlob(audioData);

        (async () => {
            await speak(blob);

            lipAnimationEnabled = true;
            lipFrameDt = prevDt;

            const hexLen    = audioData.length;   // 예: 96 000글자(hex)
            const sampleRate = 44100;  // Hz
            const channels   = 1;  // 모노
            const bytesPerSample = 2;  // 16-bit PCM

            const bytes   = hexLen / 2;                           // 48 000 byte
            const frames  = bytes / (bytesPerSample * channels);  // 24 000 frame
            const seconds = frames / sampleRate;                  // 0.5 초

            clearTimeout(pcmTimeout);

            pcmTimeout = setTimeout(() => {
                lipAnimationEnabled = false;
                setPlayAnimation(true);
            }, (seconds + 0.1) * 1000);
        })();
    }, [audioData]);

    return (
        <div className="wrapper" onContextMenu={(e) => {e.preventDefault()}}>
            <div className="loading-screen" style={{display: isLoading ? "flex" : "none"}}>
                <img id={"splash-logo"} src={"/data/TMN_Logo.png"} alt={"Tmn Logo"} className={"logo"}/>
            </div>
            <div className="screen">
                <img
                    id={"logo-img"}
                    src={"/data/TmnLogo.png"}
                    alt={"Tmn Logo"}
                    className={"logo"}
                />
                <canvas id="canvas"/>
                <div className={"chat"}>
                    <div className={"logs-container"} >
                        <div className={"logs"}>
                            <div className={"logs-top"}/>
                            {
                                logs.map((log, index) => {
                                    if (index % 2 === 0) {
                                        return <div key={index} className={"msg log-user"}>{log}</div>;
                                    } else {
                                        return (
                                            <div key={index} className={"log-bot-container"}>
                                                <span className={"bot-name"}>{personName}</span>
                                                <span className={"bot-name-gap"}/>
                                                <ChatBotLog log={log} flush={index !== logs.length - 1} onEnded={() => {
                                                    scrollToBottom();
                                                }}/>
                                            </div>
                                        );
                                    }
                                })
                            }
                            {
                                !isActive && (
                                    <div className={"log-bot-container"}>
                                        <span className={"bot-name"}>{personName} <ThinkingIndicator/></span>
                                        <div className={"msg log-bot log-bot-thinking"} onLoad={scrollToBottom}>
                                            考え中…
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                    <div className={"input-area"}>
                        {
                            (() => {
                                switch (mode) {
                                    case "recording":
                                        return (
                                            <button
                                                id={"input-button-record"}
                                                className={"viz"}
                                                onClick={onInputButton}
                                                style={{
                                                    "--alpha": Math.max(1.0 - roudness * 3.0, 0.0),
                                                }}
                                            >
                                                {
                                                    [1,2,3,4,5].map((i) => (
                                                        <span
                                                            key={i}
                                                            style={{
                                                                "--scale": Math.min(0.6 + roudness * 4.0, 1.4),
                                                            }}
                                                        />
                                                    ))
                                                }
                                            </button>
                                        );
                                    case "pause":
                                        return (
                                            <button
                                                id={"input-button-pause"}
                                                className={`viz ${!isActive && "disabled-item"}`}
                                                onClick={() => {
                                                    setmode("recording");
                                                    unlockAudio();
                                                }}
                                                disabled={!isActive}
                                            >
                                                <img src={"/data/ic_mic_2.png"} alt={"mic"}/>
                                            </button>
                                        );
                                    case "chat":
                                        return (
                                            <input id={"input-text"} type="text" placeholder={`${isActive ? "チャットを入力してください" : "回答中です…"}`} value={inputText}
                                                   className={`${!isActive && "disabled-item"}`}
                                                   disabled={!isActive}
                                                   onChange={(e) => setInputText(e.target.value)}
                                                   onKeyDown={(e) => {
                                                       if (e.key === 'Enter' && isActive) {
                                                           unlockAudio();
                                                           submitMessage();
                                                       }
                                                   }}
                                                   enterKeyHint={"send"}
                                            />
                                        );
                                }
                            })()
                        }
                        <button
                            id={"input-button"}
                            className={`button-record ${mode !== "chat" && "button-not-chat"} ${(mode !== "pause" && !isActive) && "disabled-item"}`}
                            onClick={onInputButton}
                            disabled={mode !== "pause" && !isActive}
                        >
                            { (() => {
                                switch (mode) {
                                    case "recording":
                                        return <BiPause size={60}/>
                                    case "chat":
                                        return <img src={"/data/ic_mic_2.png"} alt={"mic"}/>
                                    case "pause":
                                        return <BiX size={60}/>
                                }
                            })()}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default App;
